/**
 * Durable write queue with exponential backoff and replay.
 *
 * Lifecycle:
 *   1. axios interceptor (lib/api.js) catches network failures on POST/PUT/PATCH/DELETE
 *      and calls enqueueRequest(). Returns a synthetic { queued: true } response so
 *      the calling component does NOT throw.
 *   2. A background loop replays items whose nextRetryAt <= now.
 *   3. Replay outcomes:
 *        - 2xx                → delete item, emit 'sync' event
 *        - 4xx (except 401/408/429) → keep item, mark lastError, leave for manual retry
 *          (we DO NOT silently drop — user will see it in PendingSyncTray)
 *        - 401                → user logged out / token expired; pause this user's queue
 *        - 5xx, 408, 429, network → schedule next retry with backoff
 *   4. UI subscribes to onChange() to show live counts + tray.
 */

import {
  enqueue as idbEnqueue,
  listQueue,
  updateQueueItem,
  deleteQueueItem,
} from './idb';

const BASE = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

// Backoff schedule in ms (doubles up to a cap), per-attempt.
const BACKOFF_STEPS = [
  1_000,        // 1s
  4_000,        // 4s
  16_000,       // 16s
  60_000,       // 1m
  300_000,      // 5m
  1_800_000,    // 30m
  3_600_000,    // 1h, then capped
];

function nextBackoffMs(attempts) {
  return BACKOFF_STEPS[Math.min(attempts, BACKOFF_STEPS.length - 1)];
}

const listeners = new Set();
function emit() {
  listeners.forEach((cb) => {
    try { cb(); } catch { /* ignore */ }
  });
}

export function onChange(cb) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

/**
 * Enqueue a failed request.
 *  config: axios config from the failed call
 *  userId: current user id (so logout doesn't replay against a new account)
 */
export async function enqueueRequest(config, userId) {
  // For multipart, axios uses FormData → not directly serializable.
  // We capture the entries (text + Blobs) so the queue can rebuild on replay.
  const isMultipart = config.data instanceof FormData;
  let bodyText = null;
  let multipartEntries = null;

  if (isMultipart) {
    multipartEntries = [];
    for (const [k, v] of config.data.entries()) {
      if (v instanceof Blob) {
        // Persist the Blob directly (IndexedDB supports Blob).
        multipartEntries.push({ key: k, kind: 'blob', value: v, filename: v.name || 'file' });
      } else {
        multipartEntries.push({ key: k, kind: 'text', value: String(v) });
      }
    }
  } else if (config.data !== undefined) {
    bodyText = typeof config.data === 'string' ? config.data : JSON.stringify(config.data);
  }

  const item = {
    url: (config.baseURL || '') + (config.url || ''),
    method: (config.method || 'POST').toUpperCase(),
    headers: { ...(config.headers || {}) },
    bodyText,
    isMultipart,
    multipartEntries,
    userId: userId || null,
    label: deriveLabel(config),
  };

  const id = await idbEnqueue(item);
  emit();
  // iter201 — Register a Background Sync tag with the SW so the browser
  // drains the queue WHEN THE DEVICE COMES BACK ONLINE, even if the app
  // tab/PWA is fully closed. Chrome/Edge/Samsung Internet support this;
  // iOS Safari silently rejects the registration (queue still drains on
  // app reopen — graceful degradation).
  try {
    if (
      'serviceWorker' in navigator &&
      'SyncManager' in window
    ) {
      const reg = await navigator.serviceWorker.ready;
      if (reg && reg.sync) {
        await reg.sync.register('flush-queue');
      }
    }
  } catch {
    /* Background Sync not supported (iOS) — non-fatal, replay still
       runs on next page open via bootDurableQueue() */
  }
  // Try to replay immediately in case it was a flaky single failure.
  scheduleReplay(50);
  return { id, queued: true };
}

function deriveLabel(config) {
  const method = (config.method || 'POST').toUpperCase();
  const path = (config.url || '').split('?')[0];
  // Heuristics for human-friendly labels
  const map = [
    ['/expenses/', /(.+)/, 'Update expense'],
    ['/expenses', null, 'Add expense'],
    ['/recurring-expenses', null, 'Recurring expense'],
    ['/quick-split', null, 'Quick split'],
    ['/coin-transfers', null, 'Coin transfer'],
    ['/fund/', null, 'Fund action'],
    ['/groups', null, 'Group action'],
    ['/comments', null, 'Comment'],
    ['/files/upload', null, 'Receipt upload'],
    ['/notifications', null, 'Notification'],
  ];
  for (const [needle, , label] of map) {
    if (path.includes(needle)) return `${label} (${method})`;
  }
  return `${method} ${path}`;
}

let replayTimer = null;
let replaying = false;
let pausedUserIds = new Set();

function scheduleReplay(delayMs = 0) {
  if (replayTimer) clearTimeout(replayTimer);
  replayTimer = setTimeout(replayLoop, Math.max(0, delayMs));
}

async function replayLoop() {
  replayTimer = null;
  if (replaying) return;
  replaying = true;
  try {
    const all = await listQueue();
    const now = Date.now();
    // Process items whose nextRetryAt <= now, oldest first
    const due = all
      .filter((i) => !pausedUserIds.has(i.userId) && (i.nextRetryAt || 0) <= now)
      .sort((a, b) => (a.created_at || 0) - (b.created_at || 0));

    for (const item of due) {
      // eslint-disable-next-line no-await-in-loop
      await replayOne(item);
    }
    emit();

    // Schedule next pass for the soonest pending item
    const remaining = await listQueue();
    if (remaining.length > 0) {
      const soonest = Math.min(...remaining.map((i) => i.nextRetryAt || 0));
      const delay = Math.max(1000, soonest - Date.now());
      scheduleReplay(delay);
    }
  } finally {
    replaying = false;
  }
}

async function replayOne(item) {
  const headers = { ...(item.headers || {}) };
  // Always inject the freshest token (do not trust stale stored header)
  const token = localStorage.getItem('token');
  if (token) headers.Authorization = `Bearer ${token}`;

  let body;
  if (item.isMultipart && Array.isArray(item.multipartEntries)) {
    const fd = new FormData();
    for (const e of item.multipartEntries) {
      if (e.kind === 'blob' && e.value instanceof Blob) fd.append(e.key, e.value, e.filename);
      else fd.append(e.key, e.value);
    }
    body = fd;
    // Let the browser set the multipart boundary header
    delete headers['Content-Type'];
    delete headers['content-type'];
  } else if (item.bodyText != null) {
    body = item.bodyText;
    headers['Content-Type'] = headers['Content-Type'] || 'application/json';
  }

  let resp;
  try {
    resp = await fetch(item.url, {
      method: item.method,
      headers,
      body: ['GET', 'HEAD'].includes(item.method) ? undefined : body,
      credentials: 'omit',
    });
  } catch (netErr) {
    // Pure network failure → backoff
    await scheduleBackoff(item, `network: ${netErr.message || 'offline'}`);
    return;
  }

  if (resp.ok) {
    await deleteQueueItem(item.id);
    return;
  }

  // 401 → pause this user's queue (don't keep failing)
  if (resp.status === 401) {
    pausedUserIds.add(item.userId);
    await updateQueueItem(item.id, {
      lastError: 'auth_expired',
      nextRetryAt: Date.now() + 60 * 60 * 1000, // wait 1h before auto-retry
    });
    return;
  }

  // 408 / 429 / 5xx → retry with backoff
  if (resp.status === 408 || resp.status === 429 || (resp.status >= 500 && resp.status < 600)) {
    await scheduleBackoff(item, `${resp.status} ${resp.statusText}`);
    return;
  }

  // 4xx → server-rejected. Keep in queue for user to inspect/discard manually.
  let detail = `${resp.status}`;
  try {
    const j = await resp.json();
    detail = j.detail || JSON.stringify(j).slice(0, 200);
  } catch { /* ignore */ }
  await updateQueueItem(item.id, {
    blocked: true,
    lastError: detail,
    attempts: (item.attempts || 0) + 1,
    nextRetryAt: Number.MAX_SAFE_INTEGER, // do not auto-retry; await manual action
  });
}

async function scheduleBackoff(item, errMsg) {
  const attempts = Math.min(20, (item.attempts || 0) + 1);
  await updateQueueItem(item.id, {
    attempts,
    lastError: errMsg,
    nextRetryAt: Date.now() + nextBackoffMs(attempts),
  });
}

/* ---------- Public manual controls (PendingSyncTray uses these) ---------- */

export async function manualRetry(itemId) {
  await updateQueueItem(itemId, { nextRetryAt: Date.now(), attempts: 0, lastError: null, blocked: false });
  emit();
  scheduleReplay(0);
}

export async function discardItem(itemId) {
  await deleteQueueItem(itemId);
  emit();
}

export async function getPending(userId) {
  return listQueue(userId);
}

export function resumeUser(userId) {
  pausedUserIds.delete(userId);
  scheduleReplay(0);
}

/* ---------- Boot wiring ---------- */

let booted = false;
export function bootDurableQueue() {
  if (booted) return;
  booted = true;

  const onlineHandler = () => scheduleReplay(0);
  window.addEventListener('online', onlineHandler);
  // Also try a passive replay every 30s to recover from missed online events
  setInterval(() => scheduleReplay(0), 30_000);

  // Visibility wake-up (helps mobile when app returns to foreground)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') scheduleReplay(0);
  });

  // First pass on boot
  scheduleReplay(500);
}
